import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Brand, FacebookAd } from "@/hooks/api/use-brand-ads";
import { Calendar } from "lucide-react";
import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import AdCard from "./ad-card";

type SortOption = "newest" | "oldest" | "active_time_desc" | "active_time_asc";
type FormatFilter = "all" | string;
type StatusFilter = "all" | "active" | "inactive";
type MediaTypeFilter = "all" | "image" | "video";
type LaunchWeekFilter = "all" | string;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "active_time_desc", label: "Active time (High to Low)" },
  { value: "active_time_asc", label: "Active time (Low to High)" },
] as const;

// Format display names mapping
const FORMAT_DISPLAY_NAMES: Record<string, string> = {
  IMAGE: "Image",
  VIDEO: "Video",
  CAROUSEL: "Carousel",
  DCO: "Dynamic Creative Optimization",
  DPA: "Dynamic Product Ads",
};

interface AdCardsProps {
  results: FacebookAd[];
  brand: Brand;
  strategyAnalysis?: string;
  onClose?: () => void;
}

/**
 * Determines if an ad contains video content
 */
const hasVideoContent = (ad: FacebookAd): boolean => {
  const snapshot = ad.latest_snapshot;
  if (!snapshot) return false;

  // Check if main snapshot has video
  if (snapshot.video_hd_url) {
    return true;
  }

  // Check if any cards have videos
  return snapshot.cards.some((card) => card.video_hd_url || card.video_sd_url);
};

/**
 * Determines if an ad contains image content
 */
const hasImageContent = (ad: FacebookAd): boolean => {
  const snapshot = ad.latest_snapshot;
  if (!snapshot) return false;

  // Check if main snapshot has image
  if (snapshot.original_image_url || snapshot.resized_image_url) {
    return true;
  }

  // Check if any cards have images
  return snapshot.cards.some(
    (card) => card.original_image_url || card.resized_image_url
  );
};

export default function AdCards({ results, brand }: AdCardsProps) {
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [formatFilter, setFormatFilter] = useState<FormatFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [mediaTypeFilter, setMediaTypeFilter] =
    useState<MediaTypeFilter>("all");
  const [launchWeekFilter, setLaunchWeekFilter] =
    useState<LaunchWeekFilter>("all");
  const [selectedAnalysis, setSelectedAnalysis] = useState<string | null>(null);

  // Calculate counts for each filter option
  const filterCounts = useMemo(() => {
    if (!results || results.length === 0) {
      return {
        formats: {},
        mediaTypes: { image: 0, video: 0 },
        statuses: { active: 0, inactive: 0 },
        launchWeeks: {},
      };
    }

    // Filter out ads without media content first
    const validAds = results.filter((ad) => {
      const snapshot = ad.latest_snapshot;
      if (!snapshot) return false;

      const hasVideo =
        snapshot.video_hd_url ||
        snapshot.cards.some((card) => card.video_hd_url || card.video_sd_url);

      const hasImage =
        snapshot.resized_image_url ||
        snapshot.cards.some((card) => card.resized_image_url);

      return hasVideo || hasImage;
    });

    // Count by format
    const formats: Record<string, number> = {};
    validAds.forEach((ad) => {
      const format = ad.latest_snapshot?.display_format;
      if (format) {
        formats[format] = (formats[format] || 0) + 1;
      }
    });

    // Count by media type
    const mediaTypes = {
      image: validAds.filter(
        (ad) => hasImageContent(ad) && !hasVideoContent(ad)
      ).length,
      video: validAds.filter((ad) => hasVideoContent(ad)).length,
    };

    // Count by status
    const statuses = {
      active: validAds.filter((ad) => ad.is_active).length,
      inactive: validAds.filter((ad) => !ad.is_active).length,
    };

    // Count by launch week
    const launchWeeks: Record<string, number> = {};
    validAds.forEach((ad) => {
      if (ad.start_date) {
        const startDate = new Date(ad.start_date);
        // Get the week start date (Sunday)
        const weekStart = new Date(startDate);
        weekStart.setDate(startDate.getDate() - startDate.getDay());
        // Format as YYYY-MM-DD
        const weekKey = `${weekStart.getFullYear()}-${String(
          weekStart.getMonth() + 1
        ).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
        launchWeeks[weekKey] = (launchWeeks[weekKey] || 0) + 1;
      }
    });

    return { formats, mediaTypes, statuses, launchWeeks };
  }, [results]);

  // Dynamically generate format options based on available formats in the ads
  const formatOptions = useMemo(() => {
    if (!results || results.length === 0) {
      return [{ value: "all", label: "All formats", count: 0 }];
    }

    const formatSet = new Set<string>();

    results.forEach((ad) => {
      if (ad.latest_snapshot?.display_format) {
        formatSet.add(ad.latest_snapshot.display_format);
      }
    });

    const options = [
      {
        value: "all",
        label: "All formats",
        count: results.length,
      },
    ];

    // Add options for formats that exist in the data
    Array.from(formatSet)
      .sort()
      .forEach((format) => {
        options.push({
          value: format,
          label: FORMAT_DISPLAY_NAMES[format] || format,
          count: filterCounts.formats[format] || 0,
        });
      });

    return options;
  }, [results, filterCounts.formats]);

  // Generate media type options with counts
  const mediaTypeOptionsWithCounts = useMemo(() => {
    const totalCount = results ? results.length : 0;
    return [
      { value: "all", label: "All media types", count: totalCount },
      {
        value: "image",
        label: "Images only",
        count: filterCounts.mediaTypes.image,
      },
      {
        value: "video",
        label: "Videos only",
        count: filterCounts.mediaTypes.video,
      },
    ];
  }, [results, filterCounts.mediaTypes]);

  // Generate status options with counts
  const statusOptionsWithCounts = useMemo(() => {
    const totalCount = results ? results.length : 0;
    return [
      { value: "all", label: "All status", count: totalCount },
      { value: "active", label: "Active", count: filterCounts.statuses.active },
      {
        value: "inactive",
        label: "Inactive",
        count: filterCounts.statuses.inactive,
      },
    ];
  }, [results, filterCounts.statuses]);

  // Generate launch week options with counts
  const launchWeekOptionsWithCounts = useMemo(() => {
    if (!results || results.length === 0) {
      return [{ value: "all", label: "All launch weeks", count: 0 }];
    }

    const options = [
      { value: "all", label: "All launch weeks", count: results.length },
    ];

    // Get all launch weeks and sort them in descending order (newest first)
    const weeks = Object.entries(filterCounts.launchWeeks || {})
      .map(([weekKey, count]) => {
        const weekDate = new Date(weekKey);
        const weekEnd = new Date(weekDate);
        weekEnd.setDate(weekDate.getDate() + 6);

        // Format as "MMM D - MMM D, YYYY" (e.g., "Jan 1 - Jan 7, 2023")
        const formattedLabel = `${weekDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })} - ${weekEnd.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;

        return {
          value: weekKey,
          label: formattedLabel,
          count,
          date: weekDate, // For sorting
        };
      })
      .sort((a, b) => b.date.getTime() - a.date.getTime());

    return [...options, ...weeks];
  }, [results, filterCounts.launchWeeks]);

  const filteredAndSortedResults = useMemo(() => {
    if (!results) return [];

    let filtered = [...results].filter((ad) => {
      // Skip ads without any media content
      const snapshot = ad.latest_snapshot;
      if (!snapshot) return false;

      const hasVideo =
        snapshot.video_hd_url ||
        snapshot.cards.some((card) => card.video_hd_url || card.video_sd_url);

      const hasImage =
        snapshot.resized_image_url ||
        snapshot.cards.some((card) => card.resized_image_url);

      return hasVideo || hasImage;
    });

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((ad) => {
        return statusFilter === "active" ? ad.is_active : !ad.is_active;
      });
    }

    // Apply format filter
    if (formatFilter !== "all") {
      filtered = filtered.filter((ad) => {
        const snapshot = ad.latest_snapshot;
        if (!snapshot) return false;

        return snapshot.display_format === formatFilter;
      });
    }

    // Apply media type filter
    if (mediaTypeFilter !== "all") {
      filtered = filtered.filter((ad) => {
        if (mediaTypeFilter === "video") {
          return hasVideoContent(ad);
        } else if (mediaTypeFilter === "image") {
          return hasImageContent(ad) && !hasVideoContent(ad); // Only pure image ads
        }
        return true;
      });
    }

    // Apply launch week filter
    if (launchWeekFilter !== "all") {
      filtered = filtered.filter((ad) => {
        if (!ad.start_date) return false;

        const startDate = new Date(ad.start_date);
        // Get the week start date (Sunday)
        const weekStart = new Date(startDate);
        weekStart.setDate(startDate.getDate() - startDate.getDay());
        // Format as YYYY-MM-DD
        const weekKey = `${weekStart.getFullYear()}-${String(
          weekStart.getMonth() + 1
        ).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;

        return weekKey === launchWeekFilter;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return (
            new Date(b.start_date || 0).getTime() -
            new Date(a.start_date || 0).getTime()
          );
        case "oldest":
          return (
            new Date(a.start_date || 0).getTime() -
            new Date(b.start_date || 0).getTime()
          );
        case "active_time_desc": {
          const aTime = a.end_date
            ? new Date(a.end_date).getTime() -
              new Date(a.start_date || 0).getTime()
            : Date.now() - new Date(a.start_date || 0).getTime();
          const bTime = b.end_date
            ? new Date(b.end_date).getTime() -
              new Date(b.start_date || 0).getTime()
            : Date.now() - new Date(b.start_date || 0).getTime();
          return bTime - aTime;
        }
        case "active_time_asc": {
          const aTime = a.end_date
            ? new Date(a.end_date).getTime() -
              new Date(a.start_date || 0).getTime()
            : Date.now() - new Date(a.start_date || 0).getTime();
          const bTime = b.end_date
            ? new Date(b.end_date).getTime() -
              new Date(b.start_date || 0).getTime()
            : Date.now() - new Date(b.start_date || 0).getTime();
          return aTime - bTime;
        }
        default:
          return 0;
      }
    });

    return filtered;
  }, [
    results,
    sortBy,
    formatFilter,
    statusFilter,
    mediaTypeFilter,
    launchWeekFilter,
  ]);

  if (!results || results.length === 0) return null;

  return (
    <div>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Ad Library</h3>
          <span className="text-sm text-muted-foreground">
            (Showing {filteredAndSortedResults.length} out of {results.length}{" "}
            total ads)
          </span>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <Select
              value={sortBy}
              onValueChange={(value) => setSortBy(value as SortOption)}
            >
              <SelectTrigger className="">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={formatFilter}
              onValueChange={(value) => setFormatFilter(value)}
            >
              <SelectTrigger className="">
                <SelectValue>
                  {formatOptions.find((opt) => opt.value === formatFilter)
                    ?.label || "Format"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {formatOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center">
                      <span>{option.label}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {option.count > 0 && `(${option.count})`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={mediaTypeFilter}
              onValueChange={(value) =>
                setMediaTypeFilter(value as MediaTypeFilter)
              }
            >
              <SelectTrigger className="">
                <SelectValue>
                  {mediaTypeOptionsWithCounts.find(
                    (opt) => opt.value === mediaTypeFilter
                  )?.label || "Media Type"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {mediaTypeOptionsWithCounts.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center">
                      <span>{option.label}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {option.count > 0 && `(${option.count})`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger className="">
                <SelectValue>
                  {statusOptionsWithCounts.find(
                    (opt) => opt.value === statusFilter
                  )?.label || "Status"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {statusOptionsWithCounts.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center">
                      <span>{option.label}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {option.count > 0 && `(${option.count})`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={launchWeekFilter}
              onValueChange={(value) =>
                setLaunchWeekFilter(value as LaunchWeekFilter)
              }
            >
              <SelectTrigger className="">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  <SelectValue>
                    {launchWeekOptionsWithCounts.find(
                      (opt) => opt.value === launchWeekFilter
                    )?.label || "Launch Week"}
                  </SelectValue>
                </div>
              </SelectTrigger>
              <SelectContent>
                {launchWeekOptionsWithCounts.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center">
                      <span>{option.label}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {option.count > 0 && `(${option.count})`}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAndSortedResults.map((ad) => (
              <AdCard
                key={ad.external_ad_id}
                ad={ad}
                brand={brand}
                onAnalysisClick={(analysis) => setSelectedAnalysis(analysis)}
              />
            ))}
          </div>
        </div>
      </div>
      <Dialog
        open={!!selectedAnalysis}
        onOpenChange={() => setSelectedAnalysis(null)}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>AI Analysis</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto mt-4 pr-6">
            <div className="prose prose-sm">
              <ReactMarkdown>
                {typeof selectedAnalysis === "string" ? selectedAnalysis : ""}
              </ReactMarkdown>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
